import React, { useEffect, useMemo, useState } from "react";
import {
  GlobeHemisphereWest, Brain, TrendUp, TrendDown, Coins, Broadcast, MapPin,
  X, ArrowSquareOut, Sparkle, ShieldWarning, Lightning, Crosshair, ChartBar,
  CurrencyDollar, FlowArrow, FlagBanner, ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, ReferenceLine, Cell,
} from "recharts";
import { api, fmtPct } from "../lib/api";

// ─── Palette ────────────────────────────────────────────────────────────────
const EX_GREEN_DARK = "#064E3B";
const EX_GREEN_SOFT = "#A7F3D0";
const EX_AMBER      = "#B45309";
const EX_AMBER_SOFT = "#FDE68A";
const EX_RED        = "#991B1B";
const EX_INK        = "#0F172A";
const EX_SLATE      = "#334155";
const EX_BORDER     = "#D1D5DB";

// Synthetic deterministic FX velocity for the top-5 currencies.
function buildFxVelocity() {
  const days = 30;
  // Each pair: name, start, drift, amplitude
  const PAIRS = [
    { code: "EUR/USD", start: 1.078, drift:  0.0006, amp: 0.004 },
    { code: "GBP/USD", start: 1.262, drift:  0.0004, amp: 0.005 },
    { code: "USD/JPY", start: 153.4, drift:  0.08,   amp: 0.6   },
    { code: "USD/CNY", start: 7.262, drift: -0.0008, amp: 0.012 },
    { code: "DXY",     start: 104.1, drift:  0.025,  amp: 0.25  },
  ];
  const rows = [];
  for (let d = 0; d < days; d++) {
    const row = { day: d + 1, label: `D${d + 1}` };
    PAIRS.forEach((p, idx) => {
      const phase = (d / days) * Math.PI * 2 + idx;
      row[p.code] = +(p.start + p.drift * d + Math.sin(phase) * p.amp).toFixed(4);
    });
    rows.push(row);
  }
  return { rows, pairs: PAIRS.map((p) => p.code) };
}

// Commodity correlation panel — synthetic but cited to canonical FRED/BLS codes.
const COMMODITIES = [
  { code: "DCOILWTICO", label: "Crude · WTI",          mom: -1.4, exposure: 18, color: EX_AMBER },
  { code: "PCOPPUSDM",  label: "Copper",               mom:  3.2, exposure: 24, color: EX_GREEN_DARK },
  { code: "WPU101",     label: "Iron & Steel PPI",     mom: -0.8, exposure: 31, color: EX_RED  },
  { code: "WPU114",     label: "Construction Mfg PPI", mom:  0.4, exposure:  9, color: "#115E59" },
  { code: "PPIACO",     label: "All Commodities PPI",  mom:  0.1, exposure: 18, color: EX_SLATE },
];

// Logistics Surcharge Index · synthetic 30-day series indexed to 100 at D1.
// Captures the freight & duties pressure flowing into TLC for Direct POs.
// Inflection: surcharge spikes around Red Sea diversion (D8) and Copper rally (D18).
const LOGISTICS_SURCHARGE = (() => {
  const days = 30;
  const rows = [];
  for (let d = 0; d < days; d++) {
    const t = d / (days - 1);
    // Base climb 100 → 118, with a Red-Sea spike around D8-D12 and a softer ease
    const base = 100 + t * 18;
    const redSea = Math.exp(-Math.pow((d - 9) / 3.2, 2)) * 8;       // bell at D9
    const copper = Math.exp(-Math.pow((d - 19) / 4.0, 2)) * 4.2;    // softer bump at D19
    const idx = +(base + redSea + copper).toFixed(2);
    rows.push({ day: d + 1, label: `D${d + 1}`, surcharge: idx });
  }
  return rows;
})();

// Geopolitical sentinels with synthetic supplier-impact counts.
const GEO_EVENTS = [
  { region: "Red Sea / Bab-el-Mandeb", event: "Shipping diversions persist", severity: "RED",    suppliers: 14, narrative: "Suez throughput −34% YoY · maritime insurance up 5×" },
  { region: "Asia · China",            event: "Rare-earth export controls",   severity: "RED",    suppliers:  8, narrative: "Critical to electrification & semiconductor categories" },
  { region: "Europe · CBAM",           event: "Carbon border live · payments 2026", severity: "YELLOW", suppliers: 22, narrative: "Iron & steel imports require embedded-emissions reporting" },
  { region: "US · CHIPS Act",          event: "Tier-2 awards · $3.4B Q1",     severity: "GREEN",  suppliers: 11, narrative: "Domestic semiconductor capacity expanding" },
  { region: "Latin America",           event: "Copper labor unrest (Chile)",  severity: "YELLOW", suppliers:  6, narrative: "Strikes at top-3 mines · backwardation widening" },
];

const SIGNAL_INBOX = [
  { source: "FRED",  code: "DCOILWTICO", title: "WTI crude printed $78.04, -1.4% MoM",         severity: "GREEN",  ago: "2h",  impact: 18 },
  { source: "BLS",   code: "WPU101",     title: "Iron & Steel PPI softens (-0.8%)",            severity: "GREEN",  ago: "4h",  impact: 31 },
  { source: "FRED",  code: "PCOPPUSDM",  title: "Copper +3.2% MoM · tightening",                severity: "RED",    ago: "6h",  impact: 24 },
  { source: "GDELT", code: "RD-SEA",     title: "Maritime traffic Red Sea diversion holding",   severity: "RED",    ago: "8h",  impact: 14 },
  { source: "ECB",   code: "EURUSD",     title: "EUR/USD probing 1.08 resistance",              severity: "YELLOW", ago: "12h", impact:  9 },
  { source: "SAM.gov", code: "DCAA-FPRA", title: "12 DOD prime FPRA renewals pending",          severity: "YELLOW", ago: "1d",  impact: 12 },
];

// ─── Component ──────────────────────────────────────────────────────────────
export default function MarketOperatingPictureView() {
  const [briefing, setBriefing] = useState(null);
  const [signals,  setSignals]  = useState(null);
  const [drawer,   setDrawer]   = useState(null);
  const [samStatus, setSamStatus] = useState(null);
  const [samFeed,   setSamFeed]   = useState(null);
  const [samRefreshing, setSamRefreshing] = useState(false);
  const [samError, setSamError] = useState(null);

  useEffect(() => {
    api.marketSignals().then(setSignals).catch(() => setSignals({ indicators: [] }));
    api.marketBriefing().then(setBriefing).catch(() => setBriefing({ briefing: null, error: true }));
    api.samStatus().then(setSamStatus).catch(() => setSamStatus({ configured: false, last_sync: null, error: true }));
    api.samFeed(25).then(setSamFeed).catch(() => setSamFeed([]));
  }, []);

  const refreshSam = async () => {
    setSamRefreshing(true);
    setSamError(null);
    try {
      await api.samRefresh();
      const [status, feed] = await Promise.all([api.samStatus(), api.samFeed(25)]);
      setSamStatus(status);
      setSamFeed(feed);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Refresh failed";
      setSamError(detail);
    } finally {
      setSamRefreshing(false);
    }
  };

  const { rows: fxRows, pairs } = useMemo(buildFxVelocity, []);

  // ─── Macro Intelligence Strip values ─────────────────────────────────────
  const dxy        = fxRows[fxRows.length - 1]?.DXY || 104.4;
  const dxyChange  = dxy - (fxRows[0]?.DXY || 104.1);
  const commodVol  = (COMMODITIES.reduce((s, c) => s + Math.abs(c.mom), 0) / COMMODITIES.length).toFixed(2);
  const geoIndex   = (
    (GEO_EVENTS.filter((e) => e.severity === "RED").length * 2.5) +
    (GEO_EVENTS.filter((e) => e.severity === "YELLOW").length * 1.5) +
    (GEO_EVENTS.filter((e) => e.severity === "GREEN").length * 0.5)
  ).toFixed(1);
  const sentimentScore = -((COMMODITIES.reduce((s, c) => s + c.mom, 0) / COMMODITIES.length) * 4).toFixed(0);

  // ─── Drill openers ───────────────────────────────────────────────────────
  const openFxDrill = (pair) => setDrawer({
    title: `FX Velocity · ${pair || "Top 5"} Portfolio Impact`,
    kind: "FX",
    rows: [
      { label: "IT services contracts · EUR-denominated", suppliers: 12, note: "Hedge ratio: 65% locked at 1.07" },
      { label: "Manufactured goods imports · CNY pegged",  suppliers:  8, note: "USD strength = +1.4% landed cost" },
      { label: "JPY-priced semiconductors",                suppliers:  4, note: "Yen weakness benefits import program" },
      { label: "Landed Cost Exposure (TLC for Direct POs)", suppliers: 24,
        dollar: 41_500_000,
        note: "Aggregate TLC at risk across FX-exposed Direct POs · freight + duties + FX pass-through" },
    ],
  });

  const openCommodityDrill = (c) => {
    // Landed Cost Exposure ≈ exposure POs × $1.85M avg × surcharge factor (commodity MoM)
    const surchargeFactor = 1 + Math.max(0, c.mom) / 100;
    const landedExposure  = Math.round(c.exposure * 1_850_000 * surchargeFactor);
    return setDrawer({
      title: `${c.label} · Portfolio Impact`,
      kind: "COMMODITY",
      rows: [
        { label: `${c.exposure} active POs reference this index`,
          suppliers: c.exposure,
          note: `${c.mom >= 0 ? "+" : ""}${c.mom}% MoM movement · index ${c.code}` },
        { label: "Landed Cost Exposure (TLC for Direct POs)",
          suppliers: c.exposure,
          dollar: landedExposure,
          note: `Total Landed Cost at risk if ${c.label} sustains its current trajectory · includes freight & duties pass-through` },
        { label: "Decision-Adjusted spend at risk",
          suppliers: Math.round(c.exposure * 2.4),
          note: c.mom >= 1 ? "Recommend forward-buy on long-lead items" : "Soft pricing — open renegotiation window" },
        { label: "FRED / BLS series · " + c.code,
          suppliers: null,
          note: "Macro driver cited inline on Performance Ledger rows" },
      ],
    });
  };

  const openGeoDrill = (g) => setDrawer({
    title: `${g.region} · ${g.event}`,
    kind: "GEO",
    rows: [
      { label: "Suppliers exposed to this event",  suppliers: g.suppliers, note: g.narrative },
      { label: "Severity classification",          suppliers: null,        note: `${g.severity} tier · escalate per playbook` },
      { label: "Landed Cost Exposure (TLC for Direct POs)",
        suppliers: g.suppliers,
        dollar: Math.round(g.suppliers * 1_850_000 * (g.severity === "RED" ? 1.18 : g.severity === "YELLOW" ? 1.08 : 1.02)),
        note: g.severity === "RED"
          ? "Surcharge + diversion premium baked into Total Landed Cost"
          : "Monitor freight & duties trajectory · TLC sensitivity flagged" },
      { label: "Recommended posture",              suppliers: null,        note: g.severity === "RED" ? "Activate dual-source plan · accelerate qualification" : g.severity === "YELLOW" ? "Tighten cadence on price-watch · weekly check-in" : "Monitor · no action required" },
    ],
  });

  const openSignalDrill = (s) => setDrawer({
    title: `${s.source} · ${s.code} signal`,
    kind: "SIGNAL",
    rows: [
      { label: s.title,                                       suppliers: s.impact, note: `${s.source} · ${s.code} · ${s.ago} ago` },
      { label: "Recommended next step",                       suppliers: null,
        note: s.severity === "RED"
          ? "Escalate to category lead · re-baseline forward exposure"
          : s.severity === "YELLOW"
            ? "Schedule weekly check-in · update playbook"
            : "Capture as tailwind in category briefing" },
    ],
  });

  return (
    <section
      className="bg-[#F9FAFB] border border-[#D1D5DB] rounded-sm"
      data-testid="market-operating-picture"
    >
      <header className="px-5 py-4 border-b border-[#D1D5DB] bg-[#F3F4F6] flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-950 flex items-center gap-2 tracking-tight">
            <GlobeHemisphereWest weight="duotone" className="text-emerald-800" size={20} />
            Market Operating Picture
          </h2>
          <p className="text-xs text-slate-700 mt-0.5 font-mono font-medium">
            Consolidated WORLD signal · FX · Commodities · Geopolitical · Macro Inbox · all overlaid on your spend exposure
          </p>
        </div>
        <span className="font-mono text-[10px] text-emerald-900 tracking-mono-label font-bold px-2.5 py-1 rounded-sm border border-emerald-700/40 bg-emerald-50">
          {signals ? "LIVE · macro feed reconciled" : "loading · macro feed"}
        </span>
      </header>

      {/* ─── Global Market Intelligence Briefing (LLM) ─────────────────── */}
      <BriefingCard briefing={briefing} />

      {/* ─── Macro Intelligence Strip ─────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E7EB]" data-testid="macro-intelligence-strip">
        <MacroPill label="Global DXY · Dollar Strength" value={dxy.toFixed(2)} delta={dxyChange} unit="" />
        <MacroPill label="Avg Commodity Volatility"     value={`${commodVol}%`}  trendUp={Number(commodVol) > 2} />
        <MacroPill label="Geopolitical Conflict Index"  value={`${geoIndex} / 10`} severity={Number(geoIndex) >= 7 ? "RED" : Number(geoIndex) >= 4 ? "YELLOW" : "GREEN"} />
        <MacroPill label="Market Signal Sentiment"      value={`${sentimentScore > 0 ? "+" : ""}${sentimentScore}`} severity={sentimentScore < 0 ? "RED" : sentimentScore < 5 ? "YELLOW" : "GREEN"} />
      </div>

      {/* ─── 4-Quadrant Center Grid ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#E5E7EB]">
        <FxVelocityCard rows={fxRows} pairs={pairs} onOpen={openFxDrill} />
        <CommodityCorrelationCard
          data={COMMODITIES}
          surcharge={LOGISTICS_SURCHARGE}
          onOpen={openCommodityDrill}
        />
        <GeopoliticalSentinelCard events={GEO_EVENTS} onOpen={openGeoDrill} />
        <SignalInboxCard signals={SIGNAL_INBOX} onOpen={openSignalDrill} />
      </div>

      {/* ─── Federal Market Live Feed · SAM.gov ────────────────────────── */}
      <FederalLiveFeedCard
        status={samStatus}
        feed={samFeed}
        onRefresh={refreshSam}
        refreshing={samRefreshing}
        error={samError}
      />

      <MopDrawer drawer={drawer} onClose={() => setDrawer(null)} />
    </section>
  );
}

/* ─── Cards ──────────────────────────────────────────────────────────────── */
function FederalLiveFeedCard({ status, feed, onRefresh, refreshing, error }) {
  // Loading state · backend hasn't responded yet. Distinct from
  // "configured: false" which means the API key is genuinely missing.
  const loading = status === null;
  const lastSync = status?.last_sync;
  const counts = status?.live_record_counts || {};
  const totalLive = Object.values(counts).reduce((s, n) => s + (n || 0), 0);
  const families = status?.target_families || {};
  const families_keys = Object.keys(families);
  const configured = status?.configured;
  const syncedAt = lastSync?.synced_at;
  const ago = syncedAt ? humanizeAgo(new Date(syncedAt)) : "never";
  const rows = Array.isArray(feed) ? feed : [];
  let badgeLabel;
  let badgeClass;
  if (loading) {
    badgeLabel = "SYNCING · LOADING FEDERAL FEED";
    badgeClass = "text-slate-700 border-slate-400/40 bg-slate-50";
  } else if (!configured) {
    badgeLabel = "NOT CONFIGURED · SAM.gov key missing";
    badgeClass = "text-slate-700 border-slate-400/40 bg-slate-50";
  } else if (lastSync?.ok) {
    badgeLabel = `LIVE · last sync ${ago} · ${totalLive.toLocaleString()} live records`;
    badgeClass = "text-emerald-900 border-emerald-700/40 bg-emerald-50";
  } else {
    badgeLabel = `PENDING · last sync ${ago}`;
    badgeClass = "text-amber-900 border-amber-700/40 bg-amber-50";
  }

  return (
    <div
      className="bg-white border-t border-[#D1D5DB] p-5"
      data-testid="federal-live-feed-card"
    >
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <FlagBanner weight="duotone" className="text-emerald-800" size={18} />
          <span className="font-display text-sm font-bold text-slate-950 tracking-tight">
            Federal Market · Live Feed
          </span>
          <span className="font-mono text-[10px] text-slate-700 tracking-mono-label font-bold uppercase">
            SAM.gov · Opportunities API
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`font-mono text-[10px] tracking-mono-label font-bold px-2 py-0.5 rounded-sm border ${badgeClass}`}
            data-testid="federal-feed-status-badge"
          >
            {badgeLabel}
          </span>
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading || refreshing || !configured}
            data-testid="federal-feed-refresh-btn"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-mono-label font-bold uppercase px-2.5 py-1 rounded-sm border border-emerald-800 bg-emerald-800 text-white hover:bg-emerald-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowsClockwise
              weight="bold"
              size={11}
              className={refreshing ? "animate-spin" : ""}
            />
            {refreshing ? "Refreshing…" : "Refresh Federal Feed"}
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mb-3 px-3 py-2 rounded-sm bg-rose-50 border border-rose-300 text-[12px] text-rose-900 font-mono"
          data-testid="federal-feed-error"
        >
          {error}
        </div>
      )}

      {/* Per-family count strip */}
      <div className="grid grid-cols-3 gap-px bg-[#E5E7EB] mb-3" data-testid="federal-feed-family-strip">
        {families_keys.map((code) => (
          <div key={code} className="bg-white px-3 py-2">
            <div className="font-mono text-[10px] text-slate-600 tracking-mono-label font-bold uppercase truncate">
              UNSPSC {code.slice(0, 2)} · NAICS {families[code].naics}
            </div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="font-display font-extrabold text-lg text-slate-950 tabular-nums">
                {(counts[code] || 0).toLocaleString()}
              </span>
              <span className="text-[11px] text-slate-700 truncate">{families[code].label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Recent solicitations table */}
      {rows.length > 0 ? (
        <div
          className="overflow-x-auto border border-[#E5E7EB] rounded-sm"
          data-testid="federal-feed-table"
        >
          <table className="w-full text-[12px] tabular-nums">
            <thead className="bg-[#F3F4F6] font-mono text-[10px] tracking-mono-label uppercase text-slate-700">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Posted</th>
                <th className="text-left px-3 py-2 font-bold">Title</th>
                <th className="text-left px-3 py-2 font-bold">Agency</th>
                <th className="text-left px-3 py-2 font-bold">Family</th>
                <th className="text-left px-3 py-2 font-bold">Type</th>
                <th className="text-right px-3 py-2 font-bold">Due</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.sam_notice_id}
                  className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB]"
                  data-testid={`federal-feed-row-${r.sam_notice_id}`}
                >
                  <td className="px-3 py-2 font-mono text-slate-700">{r.posted_date}</td>
                  <td className="px-3 py-2 text-slate-950 font-medium max-w-[420px] truncate" title={r.title}>
                    {r.title}
                  </td>
                  <td className="px-3 py-2 text-slate-700 max-w-[260px] truncate" title={r.agency}>
                    {r.agency.split(".").slice(-2).join(" · ")}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-emerald-900 tracking-mono-label uppercase">
                    {r.segment_code.slice(0, 2)} · {r.category}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.status}</td>
                  <td className="px-3 py-2 font-mono text-right text-slate-700">{r.response_due_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="px-3 py-6 text-center text-[12px] text-slate-700 italic font-mono">
          {loading
            ? "Syncing live federal solicitations…"
            : configured
            ? "No federal solicitations yet. Click Refresh to pull live records."
            : "Set SAM_GOV_API_KEY to activate the live federal feed."}
        </div>
      )}
    </div>
  );
}

function humanizeAgo(date) {
  const sec = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function BriefingCard({ briefing }) {
  const text = briefing?.briefing;
  return (
    <div className="bg-white p-5 border-b border-[#E5E7EB]" data-testid="market-briefing-card">
      <div className="flex items-baseline justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Brain weight="duotone" className="text-emerald-800" size={18} />
          <span className="font-display text-sm font-bold text-slate-950 tracking-tight">
            Global Market Intelligence Briefing
          </span>
        </div>
        {briefing?.generated_at && (
          <span className="font-mono text-[10px] text-slate-700 font-bold tracking-mono-label">
            authored by {briefing.model_label} · {new Date(briefing.generated_at).toLocaleString()}
          </span>
        )}
      </div>
      {text ? (
        <p className="text-[13.5px] leading-relaxed text-slate-950 font-medium whitespace-pre-wrap">
          {text}
        </p>
      ) : briefing?.error ? (
        <p className="text-xs text-rose-800 italic font-medium">Briefing channel is offline — macro feed remains healthy below.</p>
      ) : (
        <div className="space-y-1.5">
          <div className="h-3 skeleton rounded-sm w-3/4" />
          <div className="h-3 skeleton rounded-sm w-5/6" />
          <div className="h-3 skeleton rounded-sm w-2/3" />
        </div>
      )}
    </div>
  );
}

function MacroPill({ label, value, delta, unit, trendUp, severity }) {
  const tone = severity === "RED" ? "rose" : severity === "YELLOW" ? "amber" : severity === "GREEN" ? "emerald"
                : (typeof delta === "number" ? (delta >= 0 ? "emerald" : "rose") : "slate");
  const toneColor = {
    emerald: "text-emerald-900", amber: "text-amber-900", rose: "text-rose-900", slate: "text-slate-950",
  }[tone];
  return (
    <div className="bg-white p-4 lg:p-5" data-testid={`macro-pill-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="tracking-mono-label text-[10px] text-slate-700 font-bold">{label}</div>
      <div className={`font-display text-2xl lg:text-3xl mt-1 font-extrabold tabular-nums tracking-tight ${toneColor}`}>
        {value}{unit}
      </div>
      {typeof delta === "number" && (
        <div className={`font-mono text-[11px] mt-1 tabular-nums font-bold ${delta >= 0 ? "text-emerald-800" : "text-rose-800"}`}>
          {delta >= 0 ? "▲" : "▼"} {delta >= 0 ? "+" : ""}{delta.toFixed(2)}{unit}
        </div>
      )}
      {trendUp !== undefined && (
        <div className={`font-mono text-[11px] mt-1 font-bold ${trendUp ? "text-rose-800" : "text-emerald-800"}`}>
          {trendUp ? "▲ elevated" : "▼ contained"}
        </div>
      )}
    </div>
  );
}

function QuadrantCard({ title, subtitle, icon: Icon, children, testid, onDrill }) {
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2">
          <Icon weight="duotone" className="text-emerald-800" size={16} />
          <span className="font-display text-[13px] text-slate-950 font-bold tracking-tight">{title}</span>
        </div>
        {onDrill && (
          <button
            data-testid={`${testid}-drill`}
            onClick={() => onDrill()}
            className="opacity-60 hover:opacity-100 transition-opacity inline-flex items-center gap-1
                       text-[10px] font-mono text-emerald-900 tracking-mono-label font-bold"
          >
            drill <ArrowSquareOut weight="bold" size={10} />
          </button>
        )}
      </div>
      {subtitle && <p className="text-[11px] text-slate-700 font-mono font-medium mb-3 ml-6 tabular-nums">{subtitle}</p>}
      {children}
    </div>
  );
}

const FX_COLORS = {
  "EUR/USD": EX_GREEN_DARK, "GBP/USD": EX_AMBER, "USD/JPY": EX_RED, "USD/CNY": "#115E59", "DXY": EX_INK,
};
function FxVelocityCard({ rows, pairs, onOpen }) {
  return (
    <QuadrantCard
      title="FX Velocity · Top 5 Currencies"
      subtitle="30-day movement on the five pairs most material to your category portfolio"
      icon={CurrencyDollar}
      testid="quad-fx-velocity"
      onDrill={() => onOpen()}
    >
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}
                     onClick={(s) => s?.activePayload?.[0]?.payload && onOpen(s.activePayload[0].dataKey)}>
            <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: EX_SLATE }} tickFormatter={(d) => `D${d}`}
                   interval={5} stroke={EX_BORDER} />
            <YAxis yAxisId="major" tick={{ fontSize: 10, fill: EX_SLATE }} stroke={EX_BORDER} width={60} domain={["auto","auto"]} />
            <YAxis yAxisId="yen"   tick={{ fontSize: 10, fill: EX_AMBER }} stroke={EX_BORDER} orientation="right" width={48} domain={["auto","auto"]} />
            <Tooltip content={<HeritageTooltip />} />
            <Legend wrapperStyle={{ fontSize: 10, color: EX_INK }} iconType="plainline" />
            {pairs.filter((p) => p !== "USD/JPY").map((p) => (
              <Line key={p} type="monotone" yAxisId="major" dataKey={p} stroke={FX_COLORS[p]} strokeWidth={2} dot={false} />
            ))}
            <Line type="monotone" yAxisId="yen" dataKey="USD/JPY" stroke={FX_COLORS["USD/JPY"]} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </QuadrantCard>
  );
}

function CommodityCorrelationCard({ data, surcharge, onOpen }) {
  const surchargeStart = surcharge?.[0]?.surcharge || 100;
  const surchargeEnd   = surcharge?.[surcharge.length - 1]?.surcharge || 100;
  const surchargeDelta = +(surchargeEnd - surchargeStart).toFixed(1);
  return (
    <QuadrantCard
      title="Commodity Correlation · vs Your Spend"
      subtitle="MoM move × exposure · Logistics Surcharge trend overlays TLC pressure on Direct POs"
      icon={Coins}
      testid="quad-commodity-corr"
      onDrill={() => onOpen(data[0])}
    >
      <div className="h-[140px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 16, left: 12, bottom: 0 }}
                    onClick={(s) => s?.activePayload?.[0]?.payload && onOpen(s.activePayload[0].payload)}>
            <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: EX_SLATE }} stroke={EX_BORDER}
                   domain={[-5, 5]} tickFormatter={(v) => `${v}%`} />
            <YAxis type="category" dataKey="label" tick={{ fontSize: 10, fill: EX_INK, fontWeight: 600 }} stroke={EX_BORDER} width={130} />
            <Tooltip content={<HeritageTooltip valueFmt={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(2)}% MoM`} />} />
            <ReferenceLine x={0} stroke={EX_INK} />
            <Bar dataKey="mom" radius={[2, 2, 2, 2]} cursor="pointer">
              {data.map((d, i) => (<Cell key={i} fill={d.mom >= 0 ? EX_GREEN_DARK : EX_RED} />))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ─── Logistics Surcharge Index · 30-day trend ─────────────────── */}
      <div className="mt-3 border-t border-[#E5E7EB] pt-3" data-testid="logistics-surcharge-trend">
        <div className="flex items-baseline justify-between mb-1 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] text-slate-700 font-bold tracking-mono-label uppercase">
              Logistics Surcharge Index · 30D
            </span>
            <span
              className={`font-mono text-[10px] font-bold tracking-mono-label tabular-nums px-1.5 py-0.5 rounded-sm ${
                surchargeDelta >= 0
                  ? "text-rose-900 bg-rose-50 border border-rose-700/30"
                  : "text-emerald-900 bg-emerald-50 border border-emerald-700/30"
              }`}
            >
              {surchargeDelta >= 0 ? "▲" : "▼"} {surchargeDelta >= 0 ? "+" : ""}{surchargeDelta}
            </span>
          </div>
          <span className="font-mono text-[10px] text-slate-700 font-medium tabular-nums">
            indexed 100 = D1 · feeds TLC for Direct POs
          </span>
        </div>
        <div className="h-[68px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={surcharge} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#F1F5F9" strokeDasharray="2 2" />
              <XAxis dataKey="day" tick={{ fontSize: 9, fill: EX_SLATE }} interval={6}
                     tickFormatter={(d) => `D${d}`} stroke={EX_BORDER} height={16} />
              <YAxis tick={{ fontSize: 9, fill: EX_SLATE }} stroke={EX_BORDER}
                     domain={["auto", "auto"]} width={32} />
              <Tooltip content={<HeritageTooltip valueFmt={(v) => `${v} idx`} />} />
              <Line type="monotone" dataKey="surcharge" name="Logistics Surcharge"
                    stroke={EX_AMBER} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </QuadrantCard>
  );
}

function GeopoliticalSentinelCard({ events, onOpen }) {
  return (
    <QuadrantCard
      title="Geopolitical Sentinel · Localized Events"
      subtitle="Conflict + policy events overlaid on your supplier geography"
      icon={MapPin}
      testid="quad-geopolitical"
    >
      <ul className="space-y-2 max-h-[220px] overflow-auto thin-scroll pr-1">
        {events.map((g) => (
          <li
            key={g.region}
            data-testid={`geo-row-${g.region.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="flex items-start gap-3 border-l-2 pl-2.5 py-1 cursor-pointer hover:bg-[#F3F4F6]/40 transition-colors"
            style={{ borderLeftColor: g.severity === "RED" ? EX_RED : g.severity === "YELLOW" ? EX_AMBER : EX_GREEN_DARK }}
            onClick={() => onOpen(g)}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-[12px] font-bold text-slate-950">{g.region}</span>
                <span className={`font-mono text-[9px] tracking-mono-label font-bold tier-${g.severity}`}>{g.severity}</span>
              </div>
              <div className="text-[12px] text-slate-900 font-medium leading-snug">{g.event}</div>
              <div className="text-[10.5px] text-slate-700 font-mono font-medium mt-0.5">{g.narrative}</div>
            </div>
            <span className="font-mono text-[11px] text-slate-900 font-bold tabular-nums whitespace-nowrap shrink-0">
              {g.suppliers} suppliers
            </span>
          </li>
        ))}
      </ul>
    </QuadrantCard>
  );
}

function SignalInboxCard({ signals, onOpen }) {
  return (
    <QuadrantCard
      title="Market Signal Inbox · FRED · BLS · GDELT"
      subtitle="Latest alerts ranked by portfolio impact — click any row for the trace"
      icon={Broadcast}
      testid="quad-signal-inbox"
    >
      <ul className="space-y-1 max-h-[220px] overflow-auto thin-scroll pr-1">
        {signals.map((s, i) => (
          <li
            key={i}
            data-testid={`signal-row-${s.code}`}
            className="grid grid-cols-12 gap-2 items-baseline py-1.5 border-b border-[#E5E7EB] last:border-b-0 cursor-pointer hover:bg-[#F3F4F6]/40 transition-colors"
            onClick={() => onOpen(s)}
          >
            <span className={`col-span-1 font-mono text-[9px] font-bold tracking-mono-label tier-${s.severity}`}>{s.source}</span>
            <span className="col-span-2 font-mono text-[9.5px] text-slate-700 font-bold tracking-mono-label">{s.code}</span>
            <span className="col-span-7 text-[12px] text-slate-950 font-medium leading-snug">{s.title}</span>
            <span className="col-span-1 font-mono text-[10px] text-slate-700 font-bold text-right">{s.ago}</span>
            <span className="col-span-1 font-mono text-[10px] text-emerald-900 font-bold text-right tabular-nums">
              {s.impact} POs
            </span>
          </li>
        ))}
      </ul>
    </QuadrantCard>
  );
}

/* ─── Strategic Detail Drawer ────────────────────────────────────────────── */
function MopDrawer({ drawer, onClose }) {
  if (!drawer) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="mop-drawer">
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-xl h-full bg-[#F9FAFB] border-l border-[#D1D5DB] flex flex-col"
      >
        <header className="px-5 py-4 border-b border-[#D1D5DB] bg-[#F3F4F6] flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-base font-bold text-slate-950 flex items-center gap-2 tracking-tight">
              <FlowArrow weight="duotone" className="text-emerald-800" size={18} />
              {drawer.title}
            </h3>
            <p className="text-[11px] text-slate-700 mt-0.5 font-mono font-medium">
              Strategic Detail · Impact on Portfolio
            </p>
          </div>
          <button onClick={onClose} data-testid="mop-drawer-close"
                  className="p-1 rounded-sm text-slate-700 hover:text-slate-950 hover:bg-[#E5E7EB]">
            <X size={16} weight="bold" />
          </button>
        </header>
        <div className="overflow-auto thin-scroll flex-1 p-5 bg-white">
          <ul className="space-y-3">
            {drawer.rows.map((r, i) => (
              <li key={i} className="border-l-2 border-emerald-700/60 pl-3" data-testid={`mop-drawer-row-${i}`}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-display text-[13px] font-bold text-slate-950 tracking-tight">{r.label}</span>
                  {r.suppliers != null && (
                    <span className="font-mono text-[10px] text-emerald-900 font-bold tracking-mono-label tabular-nums">
                      · {r.suppliers} affected
                    </span>
                  )}
                  {typeof r.dollar === "number" && (
                    <span
                      data-testid={`mop-drawer-row-${i}-dollar`}
                      className="font-mono text-[11px] text-emerald-900 font-bold tracking-mono-label tabular-nums
                                 px-1.5 py-0.5 rounded-sm bg-emerald-50 border border-emerald-700/30"
                    >
                      ${(r.dollar / 1_000_000).toFixed(1)}M
                    </span>
                  )}
                </div>
                <div className="text-[12.5px] text-slate-900 font-medium mt-0.5 leading-relaxed">{r.note}</div>
              </li>
            ))}
          </ul>
        </div>
      </aside>
    </div>
  );
}

/* ─── Heritage tooltip (shared) ───────────────────────────────────────────── */
function HeritageTooltip({ active, payload, label, valueFmt = (v) => v }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-sm border border-[#D1D5DB] bg-white px-3 py-2 shadow-md">
      <div className="font-mono text-[10px] text-slate-700 font-bold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-[11px] text-slate-950 font-medium">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="font-mono">{p.name}</span>
          <span className="ml-auto font-mono font-bold tabular-nums">{valueFmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}
